import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

const StudentSecurityCard = ({ student }) => {
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCodes, setShowCodes] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [gpsSettings, setGpsSettings] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);

  useEffect(() => {
    loadSecurityCodes();
    loadGpsSettings();
    loadTodayAttendance();
  }, [student]);

  const loadSecurityCodes = async () => {
    if (!student) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/security/codes/${student.username}`);
      if (response.ok) {
        const data = await response.json();
        setCodes(data);
      }
    } catch (error) {
      console.error('Error loading security codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGpsSettings = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS);
      if (response.ok) setGpsSettings(await response.json());
    } catch (error) {
      console.error('Error loading GPS attendance settings:', error);
    }
  };

  const loadTodayAttendance = async () => {
    if (!student) return;
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/${student.username}`);
      if (response.ok) {
        const records = await response.json();
        const today = new Date().toISOString().split('T')[0];
        setTodayAttendance(records.find((record) => record.date === today) || null);
      }
    } catch (error) {
      console.error('Error loading attendance status:', error);
    }
  };

  const markGpsAttendance = async (action = 'dropoff') => {
    if (!navigator.geolocation) {
      setGpsStatus('This browser does not support GPS attendance.');
      return;
    }
    setGpsLoading(true);
    setGpsStatus('Getting current GPS location...');

    navigator.geolocation.getCurrentPosition(async (position) => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/student-gps`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: student.username,
            date: new Date().toISOString().split('T')[0],
            action,
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            }
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'GPS attendance failed');
        setTodayAttendance(data.attendance);
        setGpsStatus(`${action === 'pickup' ? 'Pickup' : 'Drop-off'} attendance marked by GPS.`);
      } catch (error) {
        setGpsStatus(error.message || 'GPS attendance failed.');
      } finally {
        setGpsLoading(false);
      }
    }, (error) => {
      setGpsLoading(false);
      setGpsStatus(error.message || 'Location permission is required for GPS attendance.');
    }, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    });
  };

  const toggleCodesVisibility = () => {
    setShowCodes(!showCodes);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100 hover:shadow-2xl cursor-pointer">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-blue-100 hover:shadow-2xl transition duration-300">
      {/* Header - Always visible */}
      <div
        className="p-6 cursor-pointer flex items-center justify-between"
        onClick={toggleExpanded}
      >
        <div>
          <h2 className="text-xl font-semibold text-blue-800">Security Codes</h2>
          <p className="text-gray-600 text-sm">Today's security codes for school dropoff and pickup</p>
        </div>
        <div className="flex items-center">
          <span className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </div>

      {/* Collapsible Content */}
      <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 pb-6">
          {codes && codes.dropoff_code ? (
            <div className="space-y-4">
              {/* Security Notice */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center text-yellow-800 text-sm">
                  <span className="mr-2">Warning</span>
                  <span>Share these codes only with your parents/guardians</span>
                </div>
              </div>

              {/* Codes Display */}
              <div className="space-y-3">
                {/* Dropoff Code */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div>
                    <span className="text-sm font-medium text-blue-800">Dropoff Code:</span>
                    <div className="flex items-center mt-1">
                      {showCodes ? (
                        <span className="text-2xl font-mono font-bold text-blue-900 mr-2">
                          {codes.dropoff_code}
                        </span>
                      ) : (
                        <span className="text-2xl font-mono font-bold text-blue-900 mr-2">
                          ??????
                        </span>
                      )}
                      {codes.dropoff_verified && (
                        <span className="text-green-600 text-sm flex items-center">
                          ? Verified
                          {codes.dropoff_verified_at && (
                            <span className="ml-1 text-xs text-green-500">
                              ({new Date(codes.dropoff_verified_at).toLocaleTimeString()})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pickup Code */}
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <span className="text-sm font-medium text-green-800">Pickup Code:</span>
                    <div className="flex items-center mt-1">
                      {showCodes ? (
                        <span className="text-2xl font-mono font-bold text-green-900 mr-2">
                          {codes.pickup_code}
                        </span>
                      ) : (
                        <span className="text-2xl font-mono font-bold text-green-900 mr-2">
                          ??????
                        </span>
                      )}
                      {codes.pickup_verified && (
                        <span className="text-green-600 text-sm flex items-center">
                          ? Verified
                          {codes.pickup_verified_at && (
                            <span className="ml-1 text-xs text-green-500">
                              ({new Date(codes.pickup_verified_at).toLocaleTimeString()})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Show/Hide Button */}
              <button
                onClick={toggleCodesVisibility}
                className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition duration-300 text-sm font-medium"
              >
                {showCodes ? 'Hide Codes' : 'Show Codes'}
              </button>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <p className="font-medium mb-1">Instructions:</p>
                <ul className="space-y-1">
                  <li>? Give dropoff code to parents/guardians for morning arrival</li>
                  <li>? Give pickup code for afternoon dismissal</li>
                  <li>? Codes change daily and expire at midnight</li>
                  <li>? Security staff will verify codes at school entrance</li>
                </ul>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-emerald-900">GPS Attendance</h3>
                    <p className="text-xs text-emerald-700 mt-1">
                      Mark attendance with the child's GPS location when the student is within the school radius.
                    </p>
                    {todayAttendance?.attendanceMethod === 'gps' && (
                      <p className="text-xs text-emerald-800 mt-2">
                        Marked by GPS at {todayAttendance.dropoff_time || todayAttendance.pickup_time || 'today'}.
                      </p>
                    )}
                    {gpsSettings && (
                      <p className="text-xs text-emerald-700 mt-1">
                        Range: {Math.round(Number(gpsSettings.rangeMeters || 0))}m · Accuracy required: {Math.round(Number(gpsSettings.accuracyMeters || 0))}m
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => markGpsAttendance('dropoff')}
                      disabled={gpsLoading || !gpsSettings?.enabled || !gpsSettings?.studentGpsEnabled}
                      className="bg-emerald-700 text-white px-4 py-2 rounded-lg hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      {gpsLoading ? 'Checking...' : 'Mark Drop-off by GPS'}
                    </button>
                    <button
                      type="button"
                      onClick={() => markGpsAttendance('pickup')}
                      disabled={gpsLoading || !gpsSettings?.enabled || !gpsSettings?.studentGpsEnabled}
                      className="bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      Mark Pickup by GPS
                    </button>
                  </div>
                </div>
                {gpsStatus && <p className="text-xs text-emerald-900 mt-3">{gpsStatus}</p>}
                {gpsSettings && (!gpsSettings.enabled || !gpsSettings.studentGpsEnabled) && (
                  <p className="text-xs text-amber-700 mt-3">GPS attendance is currently disabled by the school admin.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="text-4xl mb-2">Security</div>
              <p className="text-gray-500 text-sm mb-4">Security codes not available yet</p>
              <p className="text-xs text-gray-400">
                Codes are generated daily. Please check back later or contact administration.
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-5 text-left">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-emerald-900">GPS Attendance</h3>
                    <p className="text-xs text-emerald-700 mt-1">
                      Mark attendance with the child's GPS location when the student is within the school radius.
                    </p>
                    {gpsSettings && (
                      <p className="text-xs text-emerald-700 mt-1">
                        Range: {Math.round(Number(gpsSettings.rangeMeters || 0))}m · Accuracy required: {Math.round(Number(gpsSettings.accuracyMeters || 0))}m
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => markGpsAttendance('dropoff')}
                    disabled={gpsLoading || !gpsSettings?.enabled || !gpsSettings?.studentGpsEnabled}
                    className="bg-emerald-700 text-white px-4 py-2 rounded-lg hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {gpsLoading ? 'Checking...' : 'Mark Attendance by GPS'}
                  </button>
                </div>
                {gpsStatus && <p className="text-xs text-emerald-900 mt-3">{gpsStatus}</p>}
                {gpsSettings && (!gpsSettings.enabled || !gpsSettings.studentGpsEnabled) && (
                  <p className="text-xs text-amber-700 mt-3">GPS attendance is currently disabled by the school admin.</p>
                )}
              </div>
            </div>
          )}

          {/* Refresh Button */}
          <button
            onClick={loadSecurityCodes}
            className="w-full mt-4 bg-blue-100 text-blue-700 py-2 px-4 rounded-lg hover:bg-blue-200 transition duration-300 text-sm font-medium"
          >
            Refresh Codes
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentSecurityCard;
